/**
 * Map blueprints — a top-down architectural drawing of a level, generated from the map's own
 * geometry at render time.
 *
 * The landing page needs one picture per map, and there are seven maps that change whenever
 * someone edits a JSON file. A screenshot per map is a chore nobody repeats, so it goes stale
 * the day after it is taken; a painted illustration is a second art style. The map *is* data —
 * every brush, spawn and bombsite is already in `shared/maps/*.json` and already parsed on the
 * client for prediction — so the honest picture is the one drawn from it. Same projection as
 * the HUD minimap (`minimapBounds` / `worldToMinimap`: north up, uniform scale, letterboxed),
 * so what a visitor sees here is what a player sees in the corner of the screen.
 *
 * Pure: returns SVG markup for a `MapDefinition`, no DOM, so it is unit-testable and could be
 * rendered server-side for an OG image later without changes.
 */
import type { MapDefinition } from '../../../../shared/src/map/definition.js';
import { isMinimapWall, minimapBounds, worldToMinimap } from '../arena/minimap.js';

export interface BlueprintOptions {
  /** Drawing size in SVG user units (square). Default 320. */
  size?: number;
  /** Show spawn dots. Default true. */
  spawns?: boolean;
  /** Show bombsite outlines with their letters. Default true. */
  sites?: boolean;
}

/** Ink colours, chosen so the drawing reads on `--color-bg` and prints as a blueprint would. */
const INK = {
  paper: 'rgba(23, 224, 201, 0.035)',
  grid: 'rgba(23, 224, 201, 0.10)',
  gridMajor: 'rgba(23, 224, 201, 0.18)',
  floor: 'rgba(23, 224, 201, 0.06)',
  wall: 'rgba(200, 236, 232, 0.92)',
  wallLow: 'rgba(200, 236, 232, 0.45)',
  site: '#ffd400',
  spawnA: '#ff6b57',
  spawnB: '#5cb8ff',
  spawnAny: '#eceef1',
  border: 'rgba(23, 224, 201, 0.55)',
} as const;

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/** The playable footprint in metres, from the floor brushes' extent — what the card prints as "76 × 56 m". */
export function mapFootprintMeters(map: MapDefinition): { width: number; depth: number } {
  const b = minimapBounds(map.brushes, 0);
  return { width: Math.round(b.maxX - b.minX), depth: Math.round(b.maxZ - b.minZ) };
}

/**
 * Draws the map. Brushes are boxes, so each is one `<rect>` after projection; walls (anything
 * a player collides with laterally) are inked solid, low cover is inked lighter, hidden
 * collision boxes still draw — their visual is a prop, but the blueprint is about where you
 * cannot walk, and a hidden brush is exactly that.
 */
export function blueprintSvg(map: MapDefinition, opts: BlueprintOptions = {}): string {
  const size = opts.size ?? 320;
  const bounds = minimapBounds(map.brushes, 3);
  const project = (x: number, z: number) => worldToMinimap(bounds, size, x, z);

  // Metre grid, major line every 10m. Drawn in world space so it scales with the map instead
  // of being a fixed texture — a 40m map and a 90m map get visibly different grids, which is
  // the whole reason to show one.
  const grid: string[] = [];
  const startX = Math.ceil(bounds.minX / 5) * 5;
  const startZ = Math.ceil(bounds.minZ / 5) * 5;
  for (let x = startX; x <= bounds.maxX; x += 5) {
    const { px } = project(x, 0);
    grid.push(`<line x1="${fmt(px)}" y1="0" x2="${fmt(px)}" y2="${size}" stroke="${x % 10 === 0 ? INK.gridMajor : INK.grid}" stroke-width="${x % 10 === 0 ? 0.8 : 0.5}"/>`);
  }
  for (let z = startZ; z <= bounds.maxZ; z += 5) {
    const { py } = project(0, z);
    grid.push(`<line x1="0" y1="${fmt(py)}" x2="${size}" y2="${fmt(py)}" stroke="${z % 10 === 0 ? INK.gridMajor : INK.grid}" stroke-width="${z % 10 === 0 ? 0.8 : 0.5}"/>`);
  }

  const floors: string[] = [];
  const walls: string[] = [];
  // Ink order is draw order: the landing page's CSS plots each `bp-ink` element in sequence by
  // `--i`, so the plan appears the way a plotter would draw it — floors first, then walls left
  // to right, then the sites and spawns last.
  let ink = 0;
  for (const brush of [...map.brushes].sort((a, b) => a.center.x - b.center.x)) {
    const a = project(brush.center.x - brush.halfExtents.x, brush.center.z - brush.halfExtents.z);
    const b = project(brush.center.x + brush.halfExtents.x, brush.center.z + brush.halfExtents.z);
    const w = Math.max(0.75, b.px - a.px);
    const h = Math.max(0.75, b.py - a.py);
    const rect = `x="${fmt(a.px)}" y="${fmt(a.py)}" width="${fmt(w)}" height="${fmt(h)}"`;
    if (isMinimapWall(brush)) {
      const tall = brush.center.y + brush.halfExtents.y >= 1.8;
      walls.push(`<rect ${rect} class="bp-ink" style="--i:${ink++}" fill="${tall ? INK.wall : INK.wallLow}"/>`);
    } else {
      floors.push(`<rect ${rect} fill="${INK.floor}"/>`);
    }
  }

  const sites: string[] = [];
  if (opts.sites !== false) {
    for (const site of map.sites) {
      const a = project(site.center.x - site.halfExtents.x, site.center.z - site.halfExtents.z);
      const b = project(site.center.x + site.halfExtents.x, site.center.z + site.halfExtents.z);
      const w = b.px - a.px;
      const h = b.py - a.py;
      sites.push(
        `<g class="bp-ink" style="--i:${ink + 24}">` +
          `<rect x="${fmt(a.px)}" y="${fmt(a.py)}" width="${fmt(w)}" height="${fmt(h)}" fill="rgba(255,212,0,0.12)" stroke="${INK.site}" stroke-width="1.2" stroke-dasharray="3 2"/>` +
          `<text x="${fmt(a.px + w / 2)}" y="${fmt(a.py + h / 2)}" fill="${INK.site}" font-family="Oswald Variable, Arial Narrow, sans-serif" font-weight="700" font-size="${fmt(Math.max(9, size / 22))}" text-anchor="middle" dominant-baseline="central">${site.id}</text>` +
          `</g>`,
      );
    }
  }

  const spawns: string[] = [];
  if (opts.spawns !== false) {
    for (const spawn of map.spawns) {
      const { px, py } = project(spawn.position.x, spawn.position.z);
      const colour = spawn.team === 0 ? INK.spawnA : spawn.team === 1 ? INK.spawnB : INK.spawnAny;
      // A short tick in the facing direction — the drawing says where you stand AND look.
      const dx = Math.sin(spawn.yaw) * 4;
      const dz = -Math.cos(spawn.yaw) * 4;
      spawns.push(
        `<g class="bp-ink" style="--i:${ink + 30}">` +
          `<circle cx="${fmt(px)}" cy="${fmt(py)}" r="2.2" fill="${colour}"/>` +
          `<line x1="${fmt(px)}" y1="${fmt(py)}" x2="${fmt(px + dx)}" y2="${fmt(py + dz)}" stroke="${colour}" stroke-width="1"/>` +
          `</g>`,
      );
    }
  }

  const footprint = mapFootprintMeters(map);
  const scaleBarM = footprint.width >= 60 ? 20 : 10;
  const bar0 = project(bounds.minX + 2, 0).px;
  const bar1 = project(bounds.minX + 2 + scaleBarM, 0).px;
  const barY = size - 10;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" role="img" aria-label="Top-down plan of ${escapeXml(map.displayName)}">` +
    `<rect width="${size}" height="${size}" fill="${INK.paper}"/>` +
    grid.join('') +
    floors.join('') +
    walls.join('') +
    sites.join('') +
    spawns.join('') +
    `<line x1="${fmt(bar0)}" y1="${barY}" x2="${fmt(bar1)}" y2="${barY}" stroke="${INK.border}" stroke-width="1.5"/>` +
    `<line x1="${fmt(bar0)}" y1="${barY - 3}" x2="${fmt(bar0)}" y2="${barY + 3}" stroke="${INK.border}" stroke-width="1.5"/>` +
    `<line x1="${fmt(bar1)}" y1="${barY - 3}" x2="${fmt(bar1)}" y2="${barY + 3}" stroke="${INK.border}" stroke-width="1.5"/>` +
    `<text x="${fmt((bar0 + bar1) / 2)}" y="${barY - 5}" fill="${INK.border}" font-family="ui-monospace, Menlo, monospace" font-size="8" text-anchor="middle">${scaleBarM} m</text>` +
    `<text x="${size - 8}" y="14" fill="${INK.border}" font-family="ui-monospace, Menlo, monospace" font-size="8" text-anchor="end">N ↑</text>` +
    `</svg>`
  );
}

function escapeXml(text: string): string {
  return text.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c] ?? c);
}
